// Client for the custom-puzzle API. Used by:
//   - the /p/:id play route to fetch a shared puzzle (anonymous OK)
//   - the future designer UI to publish new puzzles (Phase 2)
//   - the play surface to record solves on completion

import { apiFetch } from './api';
import type { Cell, Constraint, Tile } from '../types';
import type { Badge } from './badges';

// Payload shape stored server-side and returned on GET. Matches the
// existing client Puzzle type so HexBoard renders without adapter glue.
export interface CustomPuzzlePayload {
  name: string;
  cells: Cell[];
  bag: Tile[];
  constraints: Constraint[];
}

export interface CustomPuzzleFetch {
  id: string;
  name: string;
  payload: CustomPuzzlePayload;
  author: { displayName: string } | null;
  /// True when the signed-in viewer is the puzzle's author. Drives the
  /// author-only Delete affordance on the play page.
  isMine?: boolean;
  thumbsUp: number;
  thumbsDown: number;
  playCount: number;
  /// GET-time hit counter, excluding author views. Used with playCount
  /// to compute the completion rate.
  viewCount?: number;
  /// Grid side (2, 3, or 4) — derived from cell count server-side.
  size?: number;
  /// Average elapsed time across all recorded solves, in milliseconds.
  /// Null when there are no solves yet.
  avgSolveMs?: number | null;
  createdAt: string;
}

/// Fetches a published puzzle for play. Returns null on 404 / network
/// error so the caller can render a friendly "not found" state.
export async function fetchCustomPuzzle(id: string): Promise<CustomPuzzleFetch | null> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data as CustomPuzzleFetch;
  } catch { return null; }
}

/// Records a solve. Anonymous-friendly: the server inserts an
/// unattributed row when no auth bearer is present. Returns true on
/// success; the caller treats false as fire-and-forget failure (don't
/// block the win UI on a network hiccup).
export async function recordCustomSolve(id: string, elapsedMs: number): Promise<boolean> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}/solves`, {
      method: 'POST',
      body: JSON.stringify({ elapsedMs }),
    });
    return res.ok;
  } catch { return false; }
}

// ─────────────────────────────────────────────────────────────────────
// Publish (used by the Phase 2 designer UI)
// ─────────────────────────────────────────────────────────────────────

export interface PublishBody {
  name: string;
  cells: Cell[];
  constraints: Constraint[];
}

export interface PublishResult {
  ok: boolean;
  id?: string;
  url?: string;
  /// Surfaces the server's error code so the designer UI can render a
  /// specific message ("unsolvable", "premium_required", etc.).
  error?: string;
  message?: string;
  /// Badges unlocked by this publish (created_1 / created_10 etc.).
  /// Empty when nothing new fired; the designer queues these into
  /// the achievement popup on success.
  newBadges?: Badge[];
}

/// Dry-run solver check (POST /custom/check). Used by the designer's
/// "Check Solution" button — same validation + solver as publish, but
/// no DB write, no slug, no publish. Returns ok=true on solvable;
/// ok=false with the server's error code on failure (most common:
/// 'unsolvable').
export interface CheckResult {
  ok: boolean;
  bagSize?: number;
  error?: string;
  message?: string;
}

export async function checkCustomPuzzle(body: { cells: import('../types').Cell[]; constraints: import('../types').Constraint[] }): Promise<CheckResult> {
  try {
    const res = await apiFetch('/custom/check', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: typeof data.error === 'string' ? data.error : 'unknown',
        message: typeof data.message === 'string' ? data.message : undefined,
      };
    }
    return { ok: true, bagSize: data.bagSize };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export async function publishCustomPuzzle(body: PublishBody): Promise<PublishResult> {
  try {
    const res = await apiFetch('/custom', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: typeof data.error === 'string' ? data.error : 'unknown',
        message: typeof data.message === 'string' ? data.message : undefined,
      };
    }
    return {
      ok: true,
      id: data.id,
      url: data.url,
      newBadges: Array.isArray(data.newBadges) ? (data.newBadges as Badge[]) : [],
    };
  } catch {
    return { ok: false, error: 'network' };
  }
}

// ─────────────────────────────────────────────────────────────────────
// Progress (auth-required)
// ─────────────────────────────────────────────────────────────────────

export interface CustomProgressPayload {
  cells: Cell[];
  bag: Tile[];
  solved: boolean;
  solvedAt: string | null;
  elapsedMs: number | null;
  updatedAt: string;
}

export async function loadCustomProgress(id: string): Promise<CustomProgressPayload | null> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}/progress`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.progress as CustomProgressPayload | null;
  } catch { return null; }
}

export async function saveCustomProgress(
  id: string,
  body: { cells: Cell[]; bag: Tile[]; solved?: boolean; elapsedMs?: number },
): Promise<boolean> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}/progress`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch { return false; }
}

export async function clearCustomProgress(id: string): Promise<boolean> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}/progress`, { method: 'DELETE' });
    return res.ok;
  } catch { return false; }
}

/// Returns the IDs the signed-in user has solved at least once. Used to
/// decorate Browse rows with a "✓ Solved" marker. Capped at 500
/// server-side. Returns null on auth error / network failure (caller
/// renders no decoration).
export async function fetchMyCustomSolvedIds(): Promise<string[] | null> {
  try {
    const res = await apiFetch('/custom/me/solved');
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data.ids) ? data.ids : null;
  } catch { return null; }
}

/// Deletes the caller's own custom puzzle (or any puzzle, if the caller
/// is an admin). Returns ok=false with an error code on failure.
export async function deleteCustomPuzzle(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: typeof data.error === 'string' ? data.error : 'unknown' };
    }
    return { ok: true };
  } catch { return { ok: false, error: 'network' }; }
}

// ─────────────────────────────────────────────────────────────────────
// Phase 3 — discovery, leaderboard, votes, reports, profile, admin
// ─────────────────────────────────────────────────────────────────────

export interface CustomPuzzleSummary {
  id: string;
  name: string;
  author: { displayName: string } | null;
  thumbsUp: number;
  thumbsDown: number;
  playCount: number;
  viewCount: number;
  /// Grid side (2, 3, or 4). Decoded server-side from payloadJson.
  size: number;
  /// Average elapsed time across all solves, in ms. Null when there
  /// are no recorded solves.
  avgSolveMs: number | null;
  createdAt: string;
}

export type CustomSort = 'recent' | 'top' | 'played';
/// Grid-side filter. null = show all sizes.
export type SizeFilter = 2 | 3 | 4 | null;

/// Public discovery feed. Returns lightweight summaries (no payload).
export async function fetchCustomPuzzleList(
  sort: CustomSort = 'recent',
  limit = 20,
  offset = 0,
  size: SizeFilter = null,
): Promise<{ puzzles: CustomPuzzleSummary[]; sort: CustomSort; offset: number; limit: number } | null> {
  try {
    const sizeQ = size != null ? `&size=${size}` : '';
    const res = await apiFetch(`/custom?sort=${sort}&limit=${limit}&offset=${offset}${sizeQ}`);
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

/// Signed-in user's own puzzles, including hidden ones (so the author
/// can see what's been auto-hidden).
export interface MyCustomPuzzle extends CustomPuzzleSummary {
  visibility: string;
  cleared: boolean;
}
export async function fetchMyCustomPuzzles(): Promise<MyCustomPuzzle[] | null> {
  try {
    const res = await apiFetch('/custom/mine');
    if (!res.ok) return null;
    const data = await res.json();
    return data.puzzles as MyCustomPuzzle[];
  } catch { return null; }
}

export interface LeaderboardEntry {
  displayName: string;
  elapsedMs: number;
  solvedAt: string;
}
export async function fetchLeaderboard(id: string, limit = 10): Promise<LeaderboardEntry[] | null> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}/leaderboard?limit=${limit}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.entries as LeaderboardEntry[];
  } catch { return null; }
}

export type VoteValue = 'up' | 'down';
export interface VoteResult {
  ok: boolean;
  vote?: VoteValue;
  thumbsUp?: number;
  thumbsDown?: number;
  error?: string;
}
export async function castVote(id: string, value: VoteValue): Promise<VoteResult> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}/votes`, {
      method: 'POST',
      body: JSON.stringify({ value }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: typeof data.error === 'string' ? data.error : 'unknown' };
    return data as VoteResult;
  } catch { return { ok: false, error: 'network' }; }
}

/// Clears the caller's existing vote (or returns a noop result if there
/// wasn't one). Returns the canonical post-clear thumbsUp/thumbsDown
/// counts so the UI can update without an extra fetch.
export async function clearVote(id: string): Promise<VoteResult> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}/votes`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: typeof data.error === 'string' ? data.error : 'unknown' };
    return data as VoteResult;
  } catch { return { ok: false, error: 'network' }; }
}

export async function fetchMyVote(id: string): Promise<VoteValue | null> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}/votes/me`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.vote ?? null;
  } catch { return null; }
}

export interface ReportResult {
  ok: boolean;
  recentReports?: number;
  autoHidden?: boolean;
  error?: string;
}
export async function reportCustomPuzzle(id: string, reason?: string): Promise<ReportResult> {
  try {
    const res = await apiFetch(`/custom/${encodeURIComponent(id)}/reports`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || undefined }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: typeof data.error === 'string' ? data.error : 'unknown' };
    return data as ReportResult;
  } catch { return { ok: false, error: 'network' }; }
}

// ─────────────────────────────────────────────────────────────────────
// Public profile + admin moderation
// ─────────────────────────────────────────────────────────────────────

export interface ProfileStats {
  currentStreak: number;
  longestStreak: number;
  dailySolves: number;
  daysPlayed: number;
  customSolves: number;
  customCreated: number;
}
export interface ProfileFetch {
  displayName: string;
  memberSince: string;
  stats?: ProfileStats;
  puzzles: {
    id: string; name: string;
    thumbsUp: number; thumbsDown: number;
    playCount: number; createdAt: string;
  }[];
  /// Optional so older API responses (pre-badges) still parse cleanly.
  badges?: Badge[];
  /// 2026-05-12 follow-graph fields. Optional so older API responses
  /// keep decoding. `viewerIsFollowing` / `isFollowingViewer` are null
  /// for anonymous viewers and when looking at your own profile —
  /// the component uses that to hide the follow button entirely.
  followerCount?: number;
  followingCount?: number;
  viewerIsFollowing?: boolean | null;
  isFollowingViewer?: boolean | null;
}
export async function fetchUserProfile(displayName: string): Promise<ProfileFetch | null> {
  try {
    const res = await apiFetch(`/u/${encodeURIComponent(displayName)}/profile`);
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

/// Follow / unfollow a user. Both are idempotent on the server side —
/// double-tap follows return the existing row, unfollow on a missing
/// edge is a no-op. Returns the updated follower/following counts so
/// the caller can reconcile with the server (in case the optimistic
/// flip drifted from reality).
export interface FollowResult {
  ok: boolean;
  viewerIsFollowing?: boolean;
  followerCount?: number;
  followingCount?: number;
  error?: string;
}
export async function followUser(displayName: string): Promise<FollowResult> {
  try {
    const res = await apiFetch(`/u/${encodeURIComponent(displayName)}/follow`, { method: 'POST' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: typeof data.error === 'string' ? data.error : 'unknown' };
    return data as FollowResult;
  } catch { return { ok: false, error: 'network' }; }
}
export async function unfollowUser(displayName: string): Promise<FollowResult> {
  try {
    const res = await apiFetch(`/u/${encodeURIComponent(displayName)}/follow`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: typeof data.error === 'string' ? data.error : 'unknown' };
    return data as FollowResult;
  } catch { return { ok: false, error: 'network' }; }
}

export interface AdminReportRow {
  id: string;
  name: string;
  visibility: string;
  cleared: boolean;
  thumbsUp: number;
  thumbsDown: number;
  playCount: number;
  createdAt: string;
  author: { id: string; displayName: string; email: string } | null;
  reportCount: number;
  reasons: { reason: string | null; createdAt: string }[];
}
export async function fetchAdminReports(): Promise<AdminReportRow[] | null> {
  try {
    const res = await apiFetch('/admin/reports');
    if (!res.ok) return null;
    const data = await res.json();
    return data.puzzles as AdminReportRow[];
  } catch { return null; }
}

export type AdminAction = 'clear' | 'hide' | 'unhide';
export async function adminPuzzleAction(id: string, action: AdminAction): Promise<boolean> {
  try {
    const res = await apiFetch(`/admin/custom/${encodeURIComponent(id)}/${action}`, { method: 'POST' });
    return res.ok;
  } catch { return false; }
}
